import React, { FC, useState } from "react";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import { Spin } from "antd";
import moment from "moment";
import ChartRangeSelector from "./ChartRangeSelector";
import useHttpGet from "../../hooks/useHttpGet";
import { useTypedSelector } from "../../hooks/useTypedSelector";
import { APIRoutes } from "../../http";
import CommonService from "../../services/common.service";
import {
  PeriodIndexTypes,
  PeriodTypesArray,
} from "../../services/enums";
import HighChartsService from "../../services/highcharts.service";
import { IResultResponse } from "../../models/response/result.model";
import { ICryptoIndexChart } from "../../models/cryptoindex-chart.model";
import { ICryptoIndex } from "../../models/cryptoindex.model";

interface ChartItemValues {
  date: string;
  value: number;
}
interface IProps {
  index: ICryptoIndex;
}

const IndexChart: FC<IProps> = ({ index }) => {
  const [periodIndex, setPeriodIndex] = useState(PeriodIndexTypes["1d"]);

  const indexId = useTypedSelector(({ ci }) => ci.indexId);

  const { fetchedData, loading } = useHttpGet<IResultResponse<ICryptoIndexChart>>(`${APIRoutes.CRYPTO_INDEX_CHART}/${indexId}`,
    {
      dependencies: [periodIndex, indexId],
      query: {
        Period: PeriodTypesArray[periodIndex],
      },
      condition: !!indexId,
    });

  const getOptions = (data: ICryptoIndexChart = {} as ICryptoIndexChart) => {
    return {
      ...HighChartsService.baseSettings({
        series:
          // hide moving averages when 1day selected
          periodIndex > 0 ? [
            {
              name: index.name,
              data:
                CommonService.sortByDate(data.chart)?.map(
                  (el: ChartItemValues) => [moment(el?.date).unix(), el?.value]
                ) || [],
            },
            {
              name: "MA 20 Days",
              data: CommonService.sortByDate(data.movingAverage20DaysChart)?.map(
                (el: ChartItemValues) => [moment(el?.date).unix(), el?.value]
              ),
            },
            {
              name: "MA 50 Days",
              data: CommonService.sortByDate(data.movingAverage50DaysChart)?.map(
                (el: ChartItemValues) => [moment(el?.date).unix(), el?.value]
              ),
            },
            {
              name: "MA 100 Days",
              data: CommonService.sortByDate(data.movingAverage100DaysChart)?.map(
                (el: ChartItemValues) => [moment(el?.date).unix(), el?.value]
              ),
            },
          ] :
            [{
              name: index.name,
              data:
                CommonService.sortByDate(data.chart)?.map(
                  (el: ChartItemValues) => [moment(el?.date).unix(), el?.value]
                ) || [],
            },],
      }),
      xAxis: {
        type: "datetime",
        crosshair: true,
        labels: {
          formatter: function () {
            const th: any = this;
            return moment.unix(th?.value).format("MM/DD/YYYY");
          },
        },
      },
      tooltip: {
        shared: true,
        formatter() {
          const th: any = this;

          let s: any = `${moment.unix(th?.x).format("MM/DD/YYYY hh:mm")}`;

          th?.points?.forEach((point: any) => {
            s += `<br/>${point.series.name}: ${CommonService.toLocalNum(point?.y)}`;
          });
          return s;
        },
      },
    };
  };

  return (
    <Spin spinning={loading}>
      <ChartRangeSelector
        periodIndex={periodIndex}
        setPeriodIndex={setPeriodIndex}
      />
      <HighchartsReact
        highcharts={Highcharts}
        options={getOptions(fetchedData?.result)}
      />
    </Spin>
  );
};

export default IndexChart;
